import { McButton, McIcon, McTable } from '@maersk-global/mds-react-wrapper';
import React, { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { isCarriersAvailableForTerminal } from '../../../common/helpers/carriers';
import { modifyDateWeek } from '../../../common/helpers/modify-date';
import { formattedNumberToInt } from '../../../common/helpers/utils';
import TerminalCarriersRenderer from '../../components/TerminalCarriersRenderer/TerminalCarriersRenderer';
import { defaultDataMapperYard, weeklyDataMapperYard } from '../../config/mappers';
import useCapacityData from '../../hooks/useCapacityData';
import AddDefaultValuesDrawer from '../AddDefaultValuesDrawer/AddDefaultValuesDrawer';
import EditDefaultValuesDrawer from '../EditDefaultValuesDrawer/EditDefaultValuesDrawer';
import EditWeeklyValuesDrawer from '../EditWeeklyValuesDrawer/EditWeeklyValuesDrawer';
import { filterPastEntries } from '../utils/helpers';
import { defaultValuesTableColumns, weeklyValuesTableColumns } from './const';

type YardTabProps = {
  selectedTerminal: string;
  isActive: boolean;
};

const YardTab: React.FC<YardTabProps> = (props) => {
  const { selectedTerminal, isActive } = props;
  const defaultTableRef = useRef(null);
  const weeklyTableRef = useRef(null);
  const { formatMessage } = useIntl();
  const { capacityTemplatesYard, capacityEntitiesYard, fetchCapacityForYard } = useCapacityData();
  const [selectedDefaultRow, setSelectedDefaultRow] = useState(null);
  const [selectedWeeklyData, setSelectedWeeklyData] = useState(null);
  const [selectedWeeklyRow, setSelectedWeeklyRow] = useState([]);
  const [selectedWeeklyRows, setSelectedWeeklyRows] = useState([]);
  const [selectedAddDefaultValues, setSelectedAddDefaultValues] = useState(null);
  const [olderDefaultPeriodsVisible, setOlderDefaultPeriodsVisible] = useState(false);

  const onDefaultRowClick = (e) => {
    const row = e.detail.rowData;
    setSelectedDefaultRow(row);
  };

  const onWeeklySelectChange = (e) => {
    setSelectedWeeklyRows(e.detail);
  };

  const onWeeklyRowClick = (e) => {
    const row = e.detail.rowData;

    // Check if multiple rows selected, if all numbers same then set it, else set all to 0 0 0
    setSelectedWeeklyData({
      // @ts-ignore
      yardCapacity: row.yardCapacity,
      reeferPlugs: row.reeferPlugs,
      imoUnits: row.imoUnits,
      imoUnitsDescription: row.imoUnitsDescription,
      oogUnits: row.oogUnits,
      ladenCapacity: row.ladenCapacity,
      emptyCapacity: row.emptyCapacity,
      maxCapacity: row.maxCapacity,
      capacityDataType: row.capacityDataType,
      id: row.id,
      entries: row.entries.map((x) => ({ ...x, oogUnits: x.oogUnits || 0, imoUnits: x.imoUnits || 0 })),
    });

    // @ts-ignore
    setSelectedWeeklyRow([row.date]);
  };

  const handleAddClick = () => {
    // @ts-ignore
    const lastEntry = capacityTemplatesYard[capacityTemplatesYard.length - 1];
    const latestData = {
      // @ts-ignore
      ...lastEntry.entries.find((e) => e.capacityDataType === 'TOTAL'),
      // @ts-ignore
      startDate: modifyDateWeek(lastEntry.startDate.split('T')[0], 'add', 4),
      // @ts-ignore
      previousStartDate: lastEntry.startDate.split('T')[0],
      nextStartDate: null,
      entries: lastEntry.entries || [],
    };
    // @ts-ignore
    setSelectedAddDefaultValues(latestData);
  };

  const handleMultipleEditClick = () => {
    setSelectedWeeklyData({
      // @ts-ignore
      yardCapacity: formattedNumberToInt(selectedWeeklyRows[0].yardCapacity),
      // @ts-ignore
      reeferPlugs: formattedNumberToInt(selectedWeeklyRows[0].reeferPlugs),
      // @ts-ignore
      capacityDataType: selectedWeeklyRows[0].capacityDataType,
      // @ts-ignore
      imoUnits: formattedNumberToInt(selectedWeeklyRows[0].imoUnits),
      // @ts-ignore
      oogUnits: formattedNumberToInt(selectedWeeklyRows[0].oogUnits),
      // @ts-ignore
      ladenCapacity: formattedNumberToInt(selectedWeeklyRows[0].ladenCapacity),
      // @ts-ignore
      emptyCapacity: formattedNumberToInt(selectedWeeklyRows[0].emptyCapacity),
      // @ts-ignore
      maxCapacity: formattedNumberToInt(selectedWeeklyRows[0].maxCapacity),
      // @ts-ignore
      entries: selectedWeeklyRows[0].entries,
    });
    setSelectedWeeklyRow(
      // @ts-ignore
      selectedWeeklyRows.map((row) => row.date),
    );
  };

  const defaultPeriodsTableData = useMemo(() => {
    if (!capacityTemplatesYard.length) return [];
    if (olderDefaultPeriodsVisible) {
      return capacityTemplatesYard.map(defaultDataMapperYard(capacityTemplatesYard));
    }
    const elems = filterPastEntries(capacityTemplatesYard);
    return elems.map(defaultDataMapperYard(elems));
  }, [capacityTemplatesYard, olderDefaultPeriodsVisible]);

  const shouldDisplayShowHide = useMemo(
    () => filterPastEntries(capacityTemplatesYard).length < capacityTemplatesYard.length,
    [capacityTemplatesYard],
  );

  return (
    <div className="CapacityTab">
      <div>
        <h2>{formatMessage({ id: 'yardTabHeading' })}</h2>

        <div className="CapacityTab__default-title">
          <h3>{formatMessage({ id: 'defaultValues' })}</h3>
          <McButton
            label="Add new start period"
            appearance="primary"
            icon="plus"
            variant="outlined"
            click={handleAddClick}
          />
        </div>
        <McTable
          ref={defaultTableRef}
          columns={defaultValuesTableColumns}
          expandpadding="none"
          datakey="id"
          data={defaultPeriodsTableData}
          rowclick={onDefaultRowClick}
        >
          <TerminalCarriersRenderer
            type="default"
            columns={defaultValuesTableColumns}
            ref={defaultTableRef}
            data={defaultPeriodsTableData}
            dataKey="id"
            isActive={isActive}
          />
        </McTable>
        {shouldDisplayShowHide && (
          <span
            className="CapacityTab__table-row-toggle"
            onClick={() => setOlderDefaultPeriodsVisible((current) => !current)}
          >
            <McIcon
              className="CapacityTab__table-row-toggle__icon"
              icon={`${olderDefaultPeriodsVisible ? 'chevron-up' : 'chevron-down'}`}
              size="20"
              color="#8a8a90"
            />
            {`${olderDefaultPeriodsVisible ? 'Hide' : 'Show'} older periods`}
          </span>
        )}

        <h3>{formatMessage({ id: 'weeklyValues' })}</h3>
        {!!selectedWeeklyRows.length && (
          <div className="CapacityTab__selected-rows-banner">
            <span>{formatMessage({ id: 'selectedWeeks' }, { count: selectedWeeklyRows.length })}</span>
            <McButton
              label="Edit"
              icon="pencil"
              appearance="secondary"
              variant="outlined"
              padding="compact"
              click={handleMultipleEditClick}
            />
          </div>
        )}
        <McTable
          // @ts-ignore
          rowclick={onWeeklyRowClick}
          selectchange={onWeeklySelectChange}
          select={true}
          expand={isCarriersAvailableForTerminal(selectedTerminal)}
          expandpadding="none"
          datakey="date"
          ref={weeklyTableRef}
          columns={weeklyValuesTableColumns}
          data={capacityEntitiesYard.map(weeklyDataMapperYard)}
        >
          <TerminalCarriersRenderer
            type="weekly"
            columns={weeklyValuesTableColumns}
            ref={weeklyTableRef}
            data={capacityEntitiesYard}
            dataKey="date"
            isActive={isActive}
            fetchData={() => fetchCapacityForYard(selectedTerminal)}
          />
        </McTable>
      </div>

      {selectedDefaultRow && (
        <EditDefaultValuesDrawer
          type="yard"
          // @ts-ignore
          defaultValues={selectedDefaultRow}
          selectedTerminal={selectedTerminal}
          // @ts-ignore
          id={selectedDefaultRow?.id}
          open={Boolean(selectedDefaultRow)}
          onSuccess={() => {}}
          setOpen={() => setSelectedDefaultRow(null)}
        />
      )}

      {selectedWeeklyData && (
        <EditWeeklyValuesDrawer
          type="yard"
          // @ts-ignore
          defaultValues={selectedWeeklyData}
          selectedTerminal={selectedTerminal}
          open={selectedWeeklyRow.length > 0}
          selectedWeeks={selectedWeeklyRow}
          onSuccess={() => {
            setSelectedWeeklyRows([]);
          }}
          setOpen={() => setSelectedWeeklyRow([])}
        />
      )}

      {selectedAddDefaultValues && (
        <AddDefaultValuesDrawer
          type="yard"
          // @ts-ignore
          defaultValues={selectedAddDefaultValues}
          selectedTerminal={selectedTerminal}
          // @ts-ignore
          id={selectedDefaultRow?.id}
          open={Boolean(selectedAddDefaultValues)}
          setOpen={() => setSelectedAddDefaultValues(null)}
        />
      )}
    </div>
  );
};

export default YardTab;
